import type {
  Automation,
  AutomationActionType,
  AutomationCategory,
  AutomationEvent,
  AutomationObjectType,
  AutomationRun,
  AutomationRunStatus,
  AutomationStats,
  AutomationViolation,
  CatalogField,
} from '../../entities/automation/automationTypes';
import { MOCK_GROUP_ID } from './mockSite';

/**
 * Mock automazioni — dati dal mockup `config/automazioni e insights/crm-mockup.html`
 * (tab "Elenco automazioni"), allineati al modello UI `Automation` (shape DTO
 * `AutomationDefinition`). I mock handler li serializzano via `automationToDto`.
 *
 * Le statistiche NON stanno sull'entita': sono in `mockAutomationStats`, servite
 * dall'endpoint separato GET /automation/{id}/stats. `runCount` = successi + errori.
 */
export const mockAutomations: Automation[] = [
  {
    automationDefinitionId: 1,
    name: 'Follow-up preventivo inattivo',
    description: 'Sollecita il cliente quando un preventivo inviato resta senza attività.',
    className: 'it.dvel.crm.core.model.Quotation',
    automationCategoryId: 90001,
    automationCategoryName: 'Follow-up',
    eventType: 'crm.updated',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 1,
    template: false,
    status: 'active',
    version: 1,
    mainAutomationDefinitionId: 1,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-01-15T10:00:00Z', modifiedDate: '2026-06-30T09:14:00Z',
  },
  {
    automationDefinitionId: 2,
    name: 'Assegnazione automatica lead',
    description: 'Assegna il lead al commerciale di zona e crea l’attività di contatto.',
    className: 'it.dvel.crm.core.model.Person',
    automationCategoryId: 90002,
    automationCategoryName: 'Interno',
    eventType: 'crm.created',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 2,
    template: false,
    status: 'active',
    version: 1,
    mainAutomationDefinitionId: 2,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-02-01T10:00:00Z', modifiedDate: '2026-06-30T08:10:00Z',
  },
  {
    automationDefinitionId: 3,
    name: 'Recupero cliente inattivo',
    description: 'Individua i clienti storici senza ordini recenti e avvia il recupero.',
    className: 'it.dvel.crm.core.model.Business',
    automationCategoryId: 90003,
    automationCategoryName: 'Recupero',
    eventType: 'crm.updated',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 3,
    template: false,
    status: 'suspended',
    version: 1,
    mainAutomationDefinitionId: 3,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-02-10T10:00:00Z', modifiedDate: '2026-06-29T17:42:00Z',
  },
  {
    automationDefinitionId: 4,
    name: 'Alert opportunità ferma in pipeline',
    description: 'Segnala le opportunità di valore bloccate troppo a lungo nella stessa fase.',
    className: 'it.dvel.crm.core.model.Person',
    automationCategoryId: 90004,
    automationCategoryName: 'Vendite',
    eventType: 'crm.updated',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 4,
    template: false,
    status: 'draft',
    version: 1,
    mainAutomationDefinitionId: 4,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-06-20T10:00:00Z', modifiedDate: '2026-06-20T10:00:00Z',
  },

  // --- TEMPLATE (template: true) ---
  // Prima non ce n'era nessuno e la galleria restava vuota: le card non erano
  // mai verificabili a video. Coprono le tre entita' che hanno un emblema
  // dedicato (Contatto, Preventivo, Azienda) piu' una categoria diversa
  // ciascuna, cosi' anche il filtro per categoria ha qualcosa da filtrare.
  // Id in una fascia propria (9001+) per non collidere con le automazioni reali.
  {
    automationDefinitionId: 9001,
    name: 'Benvenuto nuovo lead',
    description: 'Accoglie ogni nuovo contatto con una email dedicata.',
    className: 'it.dvel.crm.core.model.Person',
    automationCategoryId: 90002,
    automationCategoryName: 'Interno',
    eventType: 'crm.created',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 1,
    template: true,
    status: 'active',
    version: 3,
    mainAutomationDefinitionId: 9001,
    // L'unico template dei mock con una condizione: serve a vedere il rail
    // completo QUANDO/SE/ALLORA, dato che la card nasconde i passi assenti.
    conditionGroups: [
      {
        automationConditionGroupId: 990011,
        priority: 1,
        conditions: [
          {
            automationConditionId: 9900111,
            fieldName: 'marketingStatus',
            fieldType: 'ENUM',
            operator: 'EQ',
            value: '"lead"',
            valueSource: 'STATIC',
            priority: 1,
          },
        ],
      },
    ],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-01-10T10:00:00Z', modifiedDate: '2026-05-02T10:00:00Z',
  },
  {
    automationDefinitionId: 9002,
    name: 'Assegna task a nuovo preventivo',
    description: 'Crea l’attività di presa in carico appena nasce un preventivo.',
    className: 'it.dvel.crm.core.model.Quotation',
    automationCategoryId: 90004,
    automationCategoryName: 'Vendite',
    eventType: 'crm.created',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 2,
    template: true,
    status: 'active',
    version: 2,
    mainAutomationDefinitionId: 9002,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-02-14T10:00:00Z', modifiedDate: '2026-05-20T10:00:00Z',
  },
  {
    automationDefinitionId: 9003,
    name: 'Notifica azienda inattiva',
    description: 'Segnala le aziende senza attività da oltre novanta giorni.',
    className: 'it.dvel.crm.core.model.Business',
    automationCategoryId: 90003,
    automationCategoryName: 'Recupero',
    eventType: 'crm.no_activity_for_days',
    eventConfig: '{"days": 90}',
    repeatable: false,
    logEveryRecord: false,
    priority: 3,
    template: true,
    status: 'draft',
    version: 1,
    mainAutomationDefinitionId: 9003,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-03-05T10:00:00Z', modifiedDate: '2026-03-05T10:00:00Z',
  },
  {
    automationDefinitionId: 9004,
    name: 'Follow-up dopo sopralluogo',
    description: 'Ricorda di richiamare il cliente nei giorni dopo il sopralluogo.',
    className: 'it.dvel.crm.core.model.Quotation',
    automationCategoryId: 90001,
    automationCategoryName: 'Follow-up',
    eventType: 'crm.updated',
    eventConfig: '{}',
    repeatable: false,
    logEveryRecord: false,
    priority: 4,
    template: true,
    status: 'suspended',
    version: 4,
    mainAutomationDefinitionId: 9004,
    conditionGroups: [],
    actions: [],
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: '2026-01-22T10:00:00Z', modifiedDate: '2026-04-18T10:00:00Z',
  },
];

// I vocabolari enumerati stanno PRIMA dei cataloghi che li usano, e non è una preferenza di stile:
// sono `const` di modulo, quindi in temporal dead zone finché la loro riga non è stata valutata, e
// i cataloghi qui sotto sono `export const` valutati al caricamento. Dichiararli più in basso — dove
// stavano finché a servirsene era il solo blocco SE — farebbe fallire l'import con un ReferenceError
// invece che con un errore di compilazione.
const ACTIVE_INACTIVE = [
  { value: '1', label: 'Attivo' },
  { value: '0', label: 'Non attivo' },
];
const LEAD_STATUS = [
  { value: '0', label: 'Nuovo' },
  { value: '1', label: 'In corso' },
  { value: '2', label: 'Chiuso' },
];
const MARKETING_STATUS = [
  { value: '0', label: 'Lead' },
  { value: '1', label: 'Cliente' },
  { value: '2', label: 'Qualificato marketing' },
  { value: '3', label: 'Qualificato vendite' },
];
// Valori allineati a QuotationStatus dopo la rinumerazione D12: fascia 0-999 al core, di cento in
// cento. La numerazione 1..6 è quella di quando il vocabolario viveva nel plugin. Costante e non
// inline perché lo stesso vocabolario alimenta TRE cataloghi — il preventivo si filtra per stato, lo
// stato si scrive, e ora lo si attende con l'evento di transizione — e duplicarlo li farebbe
// divergere al primo ritocco. Lato server la non-duplicazione è la stessa: l'evento i valori se li
// prende dai campi filtrabili del provider.
const QUOTATION_STATUS = [
  { value: '100', label: 'Bozza' },
  { value: '200', label: 'Inviato' },
  { value: '300', label: 'Accettato' },
  { value: '400', label: 'Rifiutato' },
  { value: '500', label: 'Scaduto' },
  { value: '600', label: 'Annullato' },
];

/**
 * Catalogo QUANDO (endpoint globale /automations/object-types). SYNC con i provider OSGi
 * `AutomationObjectType` — Person, Business e Quotation, tutti e tre in `crm-automation`: il
 * provider del preventivo era nel submodule finché lo era l'entità, ed è tornato nel core con
 * TS-87 insieme a lei — e con gli `AutomationEventType` (codici namespace `crm.*`). I tre oggetti
 * espongono i 4 eventi comuni (CRUD + inattività temporale); il preventivo ne dichiara altri due,
 * la transizione di stato e i giorni che mancano alla scadenza. Le etichette qui sono in italiano;
 * in reale sono localizzate server-side dal locale dell'utente (LocaleThreadLocal).
 *
 * @param statusChanged se includere l'evento di transizione, che il solo preventivo dichiara: è la
 *                      prima entità con uno stato che descrive un percorso e non una condizione
 * @param daysBeforeDate se includere «mancano N giorni a una data», che il solo preventivo dichiara
 *                       sulla propria scadenza
 */
const catalogEvents = (statusChanged = false, daysBeforeDate = false): AutomationEvent[] => [
  { code: 'crm.created', label: 'Creato', scheduled: false, params: [] },
  { code: 'crm.updated', label: 'Modificato', scheduled: false, params: [] },
  { code: 'crm.deleted', label: 'Eliminato', scheduled: false, params: [] },
  // Stesso trigger di `crm.updated` e non alternativo a lui: un salvataggio che cambia lo stato fa
  // scattare entrambi. I valori della tendina sono quelli del campo filtrabile omonimo, che è anche
  // il modo in cui il provider li serve davvero.
  ...(statusChanged
    ? [
        {
          code: 'crm.status_changed',
          label: 'Stato modificato a',
          scheduled: false,
          params: [
            {
              name: 'status',
              label: 'Stato',
              type: 'ENUM' as const,
              required: true,
              range: false,
              enumValues: QUOTATION_STATUS,
            },
          ],
        },
      ]
    : []),
  {
    code: 'crm.no_activity_for_days',
    label: 'Nessuna attività per N giorni',
    // Schedulato: è quello su cui il builder mostra la scelta del riscatto.
    scheduled: true,
    params: [
      { name: 'days', label: 'Giorni', type: 'INTEGER', required: true, range: false, enumValues: [] },
      {
        name: 'workingDaysOnly',
        label: 'Solo giorni lavorativi',
        type: 'BOOLEAN',
        required: false,
        range: false,
        enumValues: [],
      },
    ],
  },
  // Schedulato come il precedente. Il campo data offre solo quelli che il provider sa interrogare:
  // per il preventivo la sola scadenza.
  ...(daysBeforeDate
    ? [
        {
          code: 'crm.days_before_date',
          label: 'Mancano N giorni a una data',
          scheduled: true,
          params: [
            {
              name: 'dateField',
              label: 'Campo data',
              type: 'ENUM' as const,
              required: true,
              range: false,
              enumValues: [{ value: 'dueDate', label: 'Data scadenza' }],
            },
            {
              name: 'days',
              label: 'Giorni',
              type: 'INTEGER' as const,
              required: true,
              range: false,
              enumValues: [],
            },
          ],
        },
      ]
    : []),
];

export const mockAutomationObjectTypes: AutomationObjectType[] = [
  { className: 'it.dvel.crm.core.model.Person', classNameId: 30001, label: 'Persona', events: catalogEvents() },
  { className: 'it.dvel.crm.core.model.Business', classNameId: 30002, label: 'Azienda', events: catalogEvents() },
  {
    className: 'it.dvel.crm.core.model.Quotation',
    classNameId: 30003,
    label: 'Preventivo',
    events: catalogEvents(true, true),
  },
];

/**
 * Validazione della bozza in composizione (POST /automations/{groupId}/validate) — pannello
 * Validazione del builder, per lo sviluppo **offline**.
 *
 * ATTENZIONE — questo mock è di proposito **più permissivo del server**, e il verde che dà non è
 * una promessa. Il giudice è e resta il backend (`AutomationDefinitionValidator`, l'unica classe che
 * conosce le regole): qui si riproduce soltanto ciò che serve a far *funzionare* il pannello mentre
 * si sviluppa il builder senza un Liferay acceso — cioè la **struttura** dell'automazione, non il
 * merito di ciò che contiene.
 *
 * Cosa si controlla (STRUTTURA):
 * - QUANDO: l'oggetto è scelto ed è automatizzabile; l'evento è fra quelli che quell'oggetto dichiara
 * - ALLORA: c'è almeno un'azione
 *
 * Cosa NON si controlla, e va provato contro il server (MERITO):
 * - i valori dei parametri dell'evento — obbligatorietà, forma del valore configurato, tipo. Riprodurli qui
 *   significherebbe riscrivere `Param` e `ParamType.accepts` in TypeScript e tenerli allineati a ogni
 *   regola nuova: duplicazione che cresce, e che diverge in silenzio.
 * - le condizioni del blocco SE
 *
 * Ogni violazione dichiara il **blocco** che l'ha prodotta: è così che il pannello sa dare il verde
 * a un blocco senza problemi, invece di limitarsi a elencare ciò che manca altrove.
 *
 * Messaggi in italiano come li produrrebbe il converter per it_IT.
 */
export function validateAutomationDraft(draft: {
  className?: string;
  eventType?: string;
  actions?: unknown[];
}): AutomationViolation[] {
  const className = draft.className ?? '';

  // Blocco QUANDO. I ritorni anticipati ricalcano il server: senza sapere QUALE oggetto o QUALE
  // evento, non c'è nulla contro cui validare, e gli altri blocchi non vengono nemmeno guardati.
  if (!className)
    return [{ block: 'WHEN', message: "Selezionare l'oggetto da automatizzare" }];

  const objectType = mockAutomationObjectTypes.find((o) => o.className === className);

  if (!objectType)
    return [{ block: 'WHEN', message: `L'oggetto ${className} non è automatizzabile` }];

  const event = objectType.events.find((e) => e.code === draft.eventType);

  if (!event)
    return [
      {
        block: 'WHEN',
        message: `L'evento ${draft.eventType ?? ''} non è supportato dall'oggetto ${className}`,
      },
    ];

  const violations: AutomationViolation[] = [];

  // Blocco SE: nessuna regola ancora, ed è facoltativo — un'automazione senza condizioni scatta a
  // ogni evento dell'entità, il che è legittimo.

  // Blocco ALLORA: un'automazione senza azioni scatta, valuta le condizioni e non fa nulla.
  if ((draft.actions?.length ?? 0) === 0)
    violations.push({ block: 'THEN', message: "Aggiungere almeno un'azione" });

  return violations;
}

// Catalogo SE (condition-types) per className — allineato ai provider getCatalogFields().
// Operatori derivati dal tipo (come ParamType.getOperators lato server): oggetti {code, label}
// con label già localizzata dal backend (qui in italiano, come farebbe il converter per it_IT).
// `symbol` è il simbolo di confronto che il riepilogo mostra al posto dell'etichetta per stare
// compatto. Non è localizzato (un simbolo non si traduce) ed è ASSENTE dove non ne esiste uno
// universale — CONTAINS, NOT_CONTAINS, IN, BETWEEN si dicono a parole.
const STRING_OPS = [
  { code: 'EQ', label: 'uguale a', symbol: '=', cardinality: 'SINGLE' as const },
  { code: 'NEQ', label: 'diverso da', symbol: '≠', cardinality: 'SINGLE' as const },
  { code: 'CONTAINS', label: 'contiene', cardinality: 'SINGLE' as const },
  { code: 'NOT_CONTAINS', label: 'non contiene', cardinality: 'SINGLE' as const },
];
const ENUM_OPS = [
  { code: 'EQ', label: 'uguale a', symbol: '=', cardinality: 'SINGLE' as const },
  { code: 'NEQ', label: 'diverso da', symbol: '≠', cardinality: 'SINGLE' as const },
  { code: 'IN', label: 'tra i valori', cardinality: 'SET' as const },
];
// DATE (tipo ordinato): include BETWEEN → cardinalità RANGE (editor from/to), e i relativi a oggi →
// cardinalità RELATIVE (editor quantità + unità). Stessi codici ed etichette di Operator.java.
const DATE_OPS = [
  { code: 'EQ', label: 'uguale a', symbol: '=', cardinality: 'SINGLE' as const },
  { code: 'NEQ', label: 'diverso da', symbol: '≠', cardinality: 'SINGLE' as const },
  { code: 'GT', label: 'maggiore di', symbol: '>', cardinality: 'SINGLE' as const },
  { code: 'GTE', label: 'maggiore o uguale a', symbol: '≥', cardinality: 'SINGLE' as const },
  { code: 'LT', label: 'minore di', symbol: '<', cardinality: 'SINGLE' as const },
  { code: 'LTE', label: 'minore o uguale a', symbol: '≤', cardinality: 'SINGLE' as const },
  { code: 'BETWEEN', label: 'compreso tra', cardinality: 'RANGE' as const },
  { code: 'WITHIN_NEXT', label: 'entro i prossimi', cardinality: 'RELATIVE' as const },
  { code: 'MORE_THAN_AHEAD', label: 'fra più di', cardinality: 'RELATIVE' as const },
  { code: 'WITHIN_LAST', label: 'negli ultimi', cardinality: 'RELATIVE' as const },
  { code: 'MORE_THAN_AGO', label: 'da più di', cardinality: 'RELATIVE' as const },
];
export const mockConditionTypesByClassName: Record<string, CatalogField[]> = {
  'it.dvel.crm.core.model.Person': [
    { fieldName: 'createDate', label: 'Data creazione', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'modifiedDate', label: 'Data modifica', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'externalCode', label: 'Codice esterno', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'firstName', label: 'Nome', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'lastName', label: 'Cognome', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'leadStatus', label: 'Stato lead', type: 'ENUM', operators: ENUM_OPS, enumValues: LEAD_STATUS },
    { fieldName: 'marketingStatus', label: 'Stato marketing', type: 'ENUM', operators: ENUM_OPS, enumValues: MARKETING_STATUS },
    { fieldName: 'status', label: 'Stato', type: 'ENUM', operators: ENUM_OPS, enumValues: ACTIVE_INACTIVE },
  ],
  'it.dvel.crm.core.model.Business': [
    { fieldName: 'createDate', label: 'Data creazione', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'modifiedDate', label: 'Data modifica', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'externalCode', label: 'Codice esterno', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'name', label: 'Nome', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'domain', label: 'Dominio', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'status', label: 'Stato', type: 'ENUM', operators: ENUM_OPS, enumValues: ACTIVE_INACTIVE },
  ],
  'it.dvel.crm.core.model.Quotation': [
    { fieldName: 'createDate', label: 'Data creazione', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'modifiedDate', label: 'Data modifica', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'externalCode', label: 'Codice esterno', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'title', label: 'Titolo', type: 'STRING', operators: STRING_OPS, enumValues: [] },
    { fieldName: 'dueDate', label: 'Data scadenza', type: 'DATE', operators: DATE_OPS, enumValues: [] },
    { fieldName: 'status', label: 'Stato', type: 'ENUM', operators: ENUM_OPS, enumValues: QUOTATION_STATUS },
  ],
};

/**
 * Catalogo dei campi SCRIVIBILI (/automations/updatable-fields?className=). SYNC con
 * `AutomationObjectType.getUpdatableFields()` dei provider.
 *
 * Elenco **separato** da quello dei filtrabili e non un suo sottoinsieme, e i mock lo rispecchiano:
 * le date e il codice esterno si filtrano ma non si scrivono — le prime le gestisce il framework a
 * ogni salvataggio — mentre `Person` dichiara scrivibili tutti e tre i suoi stati e `Quotation`
 * anche l'importo. In linea di principio qualunque campo può essere scrivibile: a deciderlo è il
 * provider dell'entità.
 *
 * **Senza `operators`**, come la risposta vera: servono a confrontare, e qui si assegna. È l'unica
 * differenza di forma rispetto ai campi condizione — stesso schema `CatalogField` da entrambe le
 * parti — e il tipo la modella con `operators` opzionale.
 */
export const mockUpdatableFieldsByClassName: Record<string, CatalogField[]> = {
  'it.dvel.crm.core.model.Person': [
    { fieldName: 'leadStatus', label: 'Stato lead', type: 'ENUM', enumValues: LEAD_STATUS },
    {
      fieldName: 'marketingStatus',
      label: 'Stato marketing',
      type: 'ENUM',
      enumValues: MARKETING_STATUS,
    },
    { fieldName: 'status', label: 'Stato', type: 'ENUM', enumValues: ACTIVE_INACTIVE },
  ],
  'it.dvel.crm.core.model.Business': [
    { fieldName: 'status', label: 'Stato', type: 'ENUM', enumValues: ACTIVE_INACTIVE },
  ],
  'it.dvel.crm.core.model.Quotation': [
    // Non enumerato, di proposito: in dev serve un campo che eserciti l'editor di valore NON a
    // tendina — con soli ENUM il ramo tipizzato del form non si vedrebbe mai.
    { fieldName: 'amount', label: 'Importo', type: 'MONEY', enumValues: [] },
    { fieldName: 'status', label: 'Stato', type: 'ENUM', enumValues: QUOTATION_STATUS },
  ],
};

/**
 * Codice dell'unica azione del catalogo che si dichiara NON applicabile ovunque: costante e non
 * letterale perché compare in due punti — qui e nel filtro dell'handler `action-types`, che replica
 * la regola di `UpdateFieldAutomationActionType.isApplicable` — e un refuso in uno solo dei due non
 * fallirebbe la compilazione: spegnerebbe il filtro in silenzio.
 */
export const UPDATE_FIELD_ACTION_CODE = 'crm.update-field';

/**
 * Catalogo ALLORA (/automations/action-types?className=). SYNC con i provider OSGi
 * `AutomationActionType` (package `catalog.actions`): lato server sono tre — `crm.create-task`,
 * `crm.send-email`, `crm.update-field` — e i `code` qui sono gli stessi. Non è pignoleria: su quel
 * valore il client aggancia l'editor di configurazione dell'azione (`automationActionConfig.ts`),
 * quindi un code che diverge non si manifesta come un'etichetta sbagliata ma come una modale senza
 * campi.
 *
 * Una voce in più, `crm.mock.no-parameters`, non ha un provider lato server: è l'unica azione senza
 * parametri del catalogo finto (vedi il suo commento).
 *
 * Due dettagli DIVERGONO di proposito, e servono a esercitare in dev ciò che col catalogo vero non
 * si vedrebbe: `crm.create-task` dichiara un token d'icona che la mappa del client non conosce (in
 * reale è `task`), e deve degradare sull'icona generica invece di rompere; `crm.send-email` non
 * porta descrizione, come il server fa per le azioni che non la dichiarano — la riga sotto il nome
 * non deve restare vuota. Le etichette sono in italiano; in reale le localizza il converter dal
 * locale dell'utente.
 *
 * L'elenco lo filtra per entità l'handler, come il server: la regola sta lì.
 */
export const mockAutomationActionTypes: AutomationActionType[] = [
  {
    code: UPDATE_FIELD_ACTION_CODE,
    label: 'Aggiorna campo',
    description: 'Aggiorna il valore di un campo dell’oggetto',
    icon: 'update',
    hasConfiguration: true,
    params: [],
  },
  // SENZA descrizione, di proposito: il server la omette quando l'azione non la dichiara, e in dev
  // serve una voce che eserciti quel caso — la riga non deve restare vuota sotto il nome.
  { code: 'crm.send-email', label: 'Invia email', icon: 'mail', hasConfiguration: true, params: [] },
  // Token volutamente ignoto alla mappa del client: deve degradare sull'icona generica, non rompere.
  {
    code: 'crm.create-task',
    label: 'Crea attività',
    description: 'Crea un’attività assegnata a un utente o a una coda',
    icon: 'token-inesistente',
    hasConfiguration: true,
    params: [],
  },
  // Azione SENZA parametri, che esiste solo qui: in reale ce ne sono soltanto nei moduli cliente
  // (es. `crm.fp.ask-missing-data` di Pollini), e il core non ne ha una sua. Serve a vedere in dev
  // la riga "nessun parametro" della modale, che col catalogo del core non comparirebbe mai.
  {
    code: 'crm.mock.no-parameters',
    label: 'Azione senza parametri',
    description: 'Voce di sviluppo: un’azione che non ha niente da configurare',
    icon: 'assistant',
    hasConfiguration: false,
    params: [],
  },
  // Azione con parametri DICHIARATI e senza form scritto a mano, che esiste solo qui: in dev
  // esercita il form generico della modale (ParamField), come farebbe un'azione di un plugin.
  {
    code: 'crm.mock.declared-params',
    label: 'Azione con parametri dichiarati',
    description: 'Voce di sviluppo: i parametri li disegna il form generico',
    icon: 'default',
    hasConfiguration: true,
    params: [
      { name: 'maxReminders', label: 'Solleciti massimi', type: 'INTEGER', required: true, range: false, enumValues: [] },
      {
        name: 'level',
        label: 'Livello',
        type: 'ENUM',
        required: false,
        range: false,
        enumValues: [
          { value: 'INFO', label: 'Info' },
          { value: 'WARN', label: 'Avviso' },
        ],
      },
      { name: 'urgent', label: 'Urgente', type: 'BOOLEAN', required: false, range: false, enumValues: [] },
      { name: 'amount', label: 'Importo', type: 'MONEY', required: false, range: true, enumValues: [] },
    ],
  },
];

/** Categorie del sito (dropdown builder). */
export const mockAutomationCategories: AutomationCategory[] = [
  { automationCategoryId: 90001, name: 'Follow-up', fontColor: '#ffffff', backColor: '#ff9800', priority: 10 },
  { automationCategoryId: 90002, name: 'Interno', fontColor: '#ffffff', backColor: '#9c27b0', priority: 20 },
  { automationCategoryId: 90003, name: 'Recupero', fontColor: '#ffffff', backColor: '#e91e63', priority: 30 },
  { automationCategoryId: 90004, name: 'Vendite', fontColor: '#ffffff', backColor: '#2196f3', priority: 40 },
];

const now = Date.now();
const isoAgo = (ms: number): string => new Date(now - ms).toISOString();

/**
 * Statistiche di esecuzione per automazione (endpoint /stats). `runCount` =
 * successi + errori (run completati). La bozza (id 4) non ha ancora girato.
 */
export const mockAutomationStats: Record<number, AutomationStats> = {
  1: { automationDefinitionId: 1, runCount: 1204, successCount: 1186, failureCount: 18, lastRunDate: isoAgo(6 * 60 * 1000), averageDuration: 800, actionExecutionCount: 3284 },
  2: { automationDefinitionId: 2, runCount: 842, successCount: 842, failureCount: 0, lastRunDate: isoAgo(60 * 60 * 1000), averageDuration: 620, actionExecutionCount: 1698 },
  3: { automationDefinitionId: 3, runCount: 310, successCount: 298, failureCount: 12, lastRunDate: isoAgo(24 * 60 * 60 * 1000), averageDuration: 940, actionExecutionCount: 742 },
  4: { automationDefinitionId: 4, runCount: 0, successCount: 0, failureCount: 0, lastRunDate: null, averageDuration: 0, actionExecutionCount: 0 },
};

// Log dei run d'esempio (dati dal mockup, schermata monitoraggio). Chiave =
// automationDefinitionId. La bozza (id 4) non ha run.
function mockRun(
  automationRunId: number,
  automationDefinitionId: number,
  startAgoMs: number,
  durationMs: number,
  processedItems: number,
  status: AutomationRunStatus,
): AutomationRun {
  return {
    automationRunId,
    automationDefinitionId,
    startDate: isoAgo(startAgoMs),
    endDate: status === 'running' ? null : isoAgo(startAgoMs - durationMs),
    duration: status === 'running' ? 0 : durationMs,
    processedItems,
    // I run finti conservano tutto: cosi' il drill-down mostra le righe che i mock forniscono,
    // senza la postilla sui record esaminati e non conservati.
    logEveryRecord: true,
    status,
    errorMessage: status === 'failed' ? 'Errore di esempio nel run' : null,
    companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
    createDate: isoAgo(startAgoMs),
    modifiedDate: isoAgo(status === 'running' ? startAgoMs : startAgoMs - durationMs),
  };
}

export const mockAutomationRuns: Record<number, AutomationRun[]> = {
  1: [
    mockRun(5001, 1, 6 * 60 * 1000, 700, 42, 'success'),
    mockRun(5002, 1, 18 * 60 * 1000, 600, 38, 'success'),
    mockRun(5003, 1, 24 * 60 * 60 * 1000, 2400, 1, 'failed'),
    mockRun(5004, 1, 26 * 60 * 60 * 1000, 900, 51, 'success'),
    mockRun(5005, 1, 27 * 60 * 60 * 1000, 100, 0, 'running'),
    // Passata conclusa che non ha trattato nulla: è quella che il pulsante «pulisci
    // esecuzioni vuote» toglie, e senza almeno una in seed non ci sarebbe modo di provarlo.
    // La 5005 non basta: è vuota ma ancora in corso, e quelle si saltano.
    mockRun(5006, 1, 30 * 60 * 1000, 50, 0, 'success'),
  ],
  2: [
    mockRun(5101, 2, 60 * 60 * 1000, 620, 30, 'success'),
    mockRun(5102, 2, 3 * 60 * 60 * 1000, 580, 27, 'success'),
  ],
  3: [
    mockRun(5201, 3, 24 * 60 * 60 * 1000, 940, 12, 'failed'),
    mockRun(5202, 3, 48 * 60 * 60 * 1000, 810, 40, 'success'),
  ],
};
